import { Injectable } from '@angular/core';
import { ReplaySubject, Observable, Subject } from 'rxjs';
import { GetUserInfoGQL, SetPreferencesGQL, GetAllUserInfoGQL } from '../data-services/graphql';
import { filter, map, take } from 'rxjs/operators';
import { UserPreferences } from 'app/models/user-preferences/user-preferences.model';
import { UserPreferencesPortfolioService } from 'app/shared/services/data-services/user-preferences-portfolio/user-preferences-portfolio.service';
import { LocalStorageService } from '../local-storage-service/local-storage.service';
import { UserPreferencesUnitSettingsService } from '../user-preferences-unit-settings/user-preferences-unit-settings.service';
import { UnitsService } from '@wind-log_library/fe-component-library';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { AbstractControl, ValidationErrors } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class UserPortfolioPreferencesService {
  private userPreferences = new ReplaySubject<any>();
  private userPortfolioId = new ReplaySubject<any>(1);
  private portfolioId: string;
  private portfolioName: string;
  private userProfile: any;
  private BACKEND_URL = environment.image_server + '/user';
  private userProfileCache$ = new ReplaySubject<any>(1);
  private isProfileCacheInitialized = false;
  public profileUpdated$ = new Subject<any>();

  constructor(
    private setPreferencesGQL: SetPreferencesGQL,
    private getUserInfoGQL: GetUserInfoGQL,
    private getAllUserInfo: GetAllUserInfoGQL,
    private userPortfolioService: UserPreferencesPortfolioService,
    private localStorageService: LocalStorageService,
    private userPreferencesUnitSettingsService: UserPreferencesUnitSettingsService,
    private unitsService: UnitsService,
    private http: HttpClient
  ) {}

  public loadPortfolioPreferences(): void {
    this.userPortfolioService.getUserPortfolioService().subscribe((data) => {
      const selectedUserPortfolioId = this.localStorageService.getSelectedPortfolioId();
      const foundSelected = data.user_portfolios.find((p) => p._id === selectedUserPortfolioId);
      const foundDefault = data.user_portfolios.find((p) => p.isDefault === true);
      const fallBack = data.user_portfolios[0];

      if (selectedUserPortfolioId && foundSelected) {
        this.changePortfolioId(selectedUserPortfolioId);
      } else if (foundDefault) {
        this.changePortfolioId(foundDefault._id);
      } else {
        this.changePortfolioId(fallBack._id);
      }
    });
  }

  public loadUserPreferences(): void {
    this.getUserInfoGQL
      .watch()
      .valueChanges.pipe(
        filter(result => !result.loading && !!result.data),
        map((results) => UserPreferences.mapPreferences(results?.data?.user?.preferences)))
      .subscribe((data) => {
        this.userPreferences.next(data);
      });
  }

  public setUserPreferences(preferences: any): void {
    this.setPreferencesGQL
      .mutate({ variables: { preferences }})
      .pipe(
        map((results) => {
          return UserPreferences.mapPreferences(results.data.setPreferences);
        })
      )
      .subscribe((data) => {
        this.userPreferences.next(data);
        this.userPortfolioService.portfolioListUpdate.next(true);
      });
  }

  public setPortfolioName(name): string {
    return (this.portfolioName = name);
  }

  public getPortfolioId(): string {
    return this.portfolioId;
  }

  public getPortfolioName(): string {
    return this.portfolioName;
  }

  public changePortfolioId(id: string) {
    this.portfolioId = id;
    this.userPortfolioId.next(id);
    this.localStorageService.setSelectedPortfolioId(id);
  }

  public getUserPortfolioPreferences(): Observable<any> {
    return this.userPortfolioId.asObservable();
  }

  public getUserPreferences(): Observable<any> {
    return this.userPreferences;
  }

  public getUserInfo(): Observable<any> {
    return this.getAllUserInfo.fetch();
  }

 public getUserProfileForSettings(): Observable<any> {
    if (!this.isProfileCacheInitialized) {
      this.initializeProfileCache();
    }
    return this.userProfileCache$.asObservable();
  }

  private initializeProfileCache(): void {
    this.getAllUserInfo.fetch().subscribe((data) => {
      const user = data.data.user;
      const profile = {
        username: user.username,
        firstName: user.name,
        lastName: user.last_name,
        email: user.email,
        company: user.attributes.company,
        position: user.position,
        country: user.attributes.country,
        image: user._id ? `${environment.image_server}/user/${user._id}.webp` : '',
      };
      this.userProfileCache$.next(profile);
      this.isProfileCacheInitialized = true;
    });
  }

  public updateLocalProfile(partialProfile: any): void {
    this.userProfileCache$.pipe(take(1)).subscribe((currentProfile) => {
      const updatedProfile = { ...currentProfile, ...partialProfile };
      this.userProfileCache$.next(updatedProfile);
      this.profileUpdated$.next(updatedProfile);
    });
  }

  public refreshProfileCache(): void {
    this.isProfileCacheInitialized = false;
    this.initializeProfileCache();
  }

  public getUserProfile(): any {
    return this.userProfile;
  }

  public uploadUserImage(userId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('image', file);
    return this.http.post(this.BACKEND_URL, formData);
  }

  public noDefaultPortfolio(control: AbstractControl): ValidationErrors | null {
    if (control.value && control.value.toLowerCase() === 'main portfolio') {
      return {
        noDefaultPortfolio: true
      };
    }
    return null;
  }

}
